import { QueryClient } from '@tanstack/react-query';

// Create a client with default options
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time in milliseconds that data remains fresh
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Time in milliseconds that unused/inactive cache data remains in memory
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Refetch on window focus
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth queries
  auth: {
    user: ['auth', 'user'] as const,
    profile: ['auth', 'profile'] as const,
  },
  
  // Property queries
  properties: {
    all: ['properties'] as const,
    list: (filters?: any) => ['properties', 'list', filters] as const,
    detail: (id: string) => ['properties', 'detail', id] as const,
    search: (query: string) => ['properties', 'search', query] as const,
    featured: ['properties', 'featured'] as const,
  },
  
  // Agent queries
  agents: {
    all: ['agents'] as const,
    list: (filters?: any) => ['agents', 'list', filters] as const,
    detail: (id: string) => ['agents', 'detail', id] as const,
    dashboard: (id: string) => ['agents', 'dashboard', id] as const,
  },
  
  // Booking queries
  bookings: {
    all: ['bookings'] as const,
    user: (userId: string) => ['bookings', 'user', userId] as const,
    agent: (agentId: string) => ['bookings', 'agent', agentId] as const,
    property: (propertyId: string) => ['bookings', 'property', propertyId] as const,
  },
  
  // User queries
  users: {
    all: ['users'] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    profile: (id: string) => ['users', 'profile', id] as const,
  },
} as const;
